/**
 * CafePod, 21 Sep 2026. The event tracking layer (CP-026).
 *
 * Minimum event set from standards 3.9: view_item_list, select_item, filter_applied,
 * sort_applied, view_promotion, add_to_cart, subscription_option_selected,
 * accordion_opened, compare_view_opened.
 *
 * Delivery: gtag('event', ...) when the Google tag is on the page, otherwise a
 * dataLayer push, plus Shopify.analytics.publish('cp_<event>') for customer
 * events pixels. Nothing here blocks rendering: the module is loaded with
 * fetchpriority="low" from snippets/cp-events.liquid.
 *
 * Items are read from the data attributes snippets/cp-card.liquid renders, and
 * on the PDP from the JSON snippets/cp-events.liquid emits. No extra requests.
 */
const script = document.currentScript || document.querySelector('script[src*="cp-events"]');
const config = {
  template: script?.dataset.template || '',
  currency: script?.dataset.currency || 'GBP',
  listId: script?.dataset.listId || '',
  listName: script?.dataset.listName || '',
};

const sent = new Set();

function send(name, params = {}) {
  const payload = { currency: config.currency, ...params };
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, payload);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...payload });
    }
  } catch (e) {
    /* never break the page for analytics */
  }
  try {
    window.Shopify?.analytics?.publish?.(`cp_${name}`, payload);
  } catch (e) {
    /* ignore */
  }
}

function itemFromCard(card, index) {
  if (!card) return null;
  return {
    item_id: card.dataset.itemId || card.dataset.variantId || '',
    item_name: card.dataset.itemName || '',
    item_variant: card.dataset.itemVariant || '',
    price: Number(card.dataset.price || 0),
    index,
  };
}

function listContextFor(el) {
  const section = el.closest('.shopify-section');
  const heading = section?.querySelector('h1, h2, h3');
  return {
    item_list_id: config.listId || section?.id || config.template,
    item_list_name: config.listName || heading?.textContent?.trim() || config.template,
  };
}

/* view_item_list: one event per section that holds cards, when it comes into view. */
function observeLists() {
  const cards = Array.from(document.querySelectorAll('[data-cp-item]'));
  const bySection = new Map();
  cards.forEach((card) => {
    const section = card.closest('.shopify-section') || document.body;
    if (!bySection.has(section)) bySection.set(section, []);
    bySection.get(section).push(card);
  });
  if (!bySection.size) return;

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const section = entry.target;
        io.unobserve(section);
        const list = bySection.get(section) || [];
        const key = `list:${section.id}`;
        if (sent.has(key) || !list.length) return;
        sent.add(key);
        send('view_item_list', {
          ...listContextFor(list[0]),
          items: list.map((card, i) => itemFromCard(card, i + 1)).filter(Boolean),
        });
      });
    },
    // Any visible part of the list counts: a 20-card grid is far taller than a phone viewport,
    // so a fractional threshold would never be met.
    { threshold: 0 }
  );
  bySection.forEach((_, section) => io.observe(section));
}

/* select_item: any click on a card that leads to the product. */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('form, button')) return;
    // Pack-size chips on a compact card swap the card in place (assets/cp-card.js); not a selection.
    if (target.closest('.cp-packs--compact a.cp-packs__chip')) return;
    const link = target.closest('product-card a, .cp-card a, .product-card__link');
    if (!link) return;
    const card = link.closest('product-card')?.querySelector('[data-cp-item]');
    if (!card) return;
    const cards = Array.from(card.closest('.shopify-section')?.querySelectorAll('[data-cp-item]') || []);
    send('select_item', { ...listContextFor(card), items: [itemFromCard(card, cards.indexOf(card) + 1)] });
  },
  { capture: true }
);

/* add_to_cart: read the form that is being submitted, before the theme's fetch. */
let pageProduct = null;
try {
  const json = document.querySelector('[data-cp-page-product]');
  if (json) pageProduct = JSON.parse(json.textContent || 'null');
} catch (e) {
  pageProduct = null;
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    const data = new FormData(form);
    const variantId = String(data.get('id') || '');
    const quantity = Number(data.get('quantity') || 1);
    const sellingPlan = String(data.get('selling_plan') || '');

    let item = null;
    const card = form.closest('product-card')?.querySelector('[data-cp-item]') || form.closest('[data-cp-item]');
    if (card) {
      item = itemFromCard(card, 1);
    } else if (pageProduct) {
      const v = pageProduct.variants.find((x) => String(x.id) === variantId) || pageProduct.variants[0];
      item = v ? { item_id: v.sku || String(v.id), item_name: pageProduct.title, item_variant: v.title, price: v.price } : null;
    }
    if (!item) return;
    item.quantity = quantity;
    send('add_to_cart', {
      value: Number((item.price * quantity).toFixed(2)),
      purchase_type: sellingPlan ? 'subscription' : 'one_time',
      selling_plan_id: sellingPlan,
      items: [item],
    });
  },
  { capture: true }
);

/* subscription_option_selected: from assets/cp-purchase.js. */
document.addEventListener('cp:purchase-change', (event) => {
  const detail = event.detail || {};
  if (!detail.purchase_type) return;
  send('subscription_option_selected', {
    purchase_type: detail.purchase_type,
    selling_plan_id: detail.selling_plan_id || '',
    cadence: detail.cadence || '',
    item_name: pageProduct?.title || document.title,
  });
});

/* accordion_opened: any details element that opens inside an accordion. */
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    if (!details.closest('.accordion, accordion-custom, .cp-avail, .product-details')) return;
    const summary = details.querySelector('summary');
    send('accordion_opened', { accordion_title: summary?.textContent?.replace(/\s+/g, ' ').trim().slice(0, 100) || '' });
  },
  { capture: true }
);

/* filter_applied, sort_applied, compare_view_opened: from Horizon's filter:update event
   (its detail carries the new query params) or, on load and back/forward, from the URL. */
let lastQuery = '';
function readUrl(initial, params) {
  const url = new URL(window.location.href);
  const searchParams = params instanceof URLSearchParams ? params : url.searchParams;
  const query = searchParams.toString();
  if (query === lastQuery && !initial) return;
  lastQuery = query;

  const filters = [];
  searchParams.forEach((value, key) => {
    if (key.startsWith('filter.')) filters.push(`${key.replace('filter.', '')}=${value}`);
  });
  if (filters.length && !sent.has(`filters:${filters.join('|')}`)) {
    sent.add(`filters:${filters.join('|')}`);
    send('filter_applied', { filters: filters.join('|'), filter_count: filters.length, item_list_id: config.listId });
  }
  const sort = searchParams.get('sort_by') || searchParams.get('sort');
  if (sort && !sent.has(`sort:${sort}`)) {
    sent.add(`sort:${sort}`);
    send('sort_applied', { sort_by: sort, item_list_id: config.listId });
  }
  if (searchParams.get('view') === 'compare' && !sent.has('compare')) {
    sent.add('compare');
    send('compare_view_opened', { item_list_id: config.listId });
  }
}
document.addEventListener('filter:update', (event) => readUrl(false, event?.detail?.queryParams));
window.addEventListener('popstate', () => readUrl(false));

/* view_promotion: announcement bar slides, the promo block and the Fix card, once each when seen. */
function observePromotions() {
  const promos = document.querySelectorAll('.announcement-bar__slide, [data-cp-promo], .cp-fix');
  if (!promos.length) return;
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        if (el.getAttribute('aria-hidden') === 'true') return;
        io.unobserve(el);
        const name = el.textContent?.replace(/\s+/g, ' ').trim().slice(0, 100) || '';
        const key = `promo:${name}`;
        if (!name || sent.has(key)) return;
        sent.add(key);
        send('view_promotion', {
          promotion_name: name,
          creative_slot: el.classList.contains('cp-fix') ? 'fix_card' : el.hasAttribute('data-cp-promo') ? 'promo_block' : 'announcement_bar',
        });
      });
    },
    { threshold: 0.5 }
  );
  promos.forEach((el) => io.observe(el));
}

function init() {
  observeLists();
  observePromotions();
  readUrl(true);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
